import { type QConfigTableData } from '@exam-management/common/types';
import { QBankStructureLevelCategory, QConfigStructureCategory } from '@exam-management/graphql';
import { Loading } from '@shared/components';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useMemo, useState } from 'react';

import { useGetSingleQuestionConditionsData } from '../../hooks';
import type { BaseQuestionConditionsContextType } from '../../types';
import {
  AddSingleQuestionConditionsFormProvider,
  type AddSingleQuestionConditionsFormType,
  type QuestionCountType,
} from '../add-single-question-conditions-form-provider';

interface AddSingleQuestionConditionsContextType extends BaseQuestionConditionsContextType {
  defaultLOS: string;
  questionCount: {
    get: QuestionCountType;
    set: (questionCount: QuestionCountType) => void;
  };
}

export interface ContentTypeMap {
  TOPIC: 'TOPIC';
  LOS: 'LOS';
}

export const AddSingleQuestionConditionsContext = createContext<
  AddSingleQuestionConditionsContextType | undefined
>(undefined);

interface AddSingleQuestionConditionsProviderProps extends PropsWithChildren {
  chapterId: string;
  initialValues?: {
    rows: QConfigTableData[];
    data: QConfigTableData;
  };
}

export function AddSingleQuestionConditionsProvider(
  props: Readonly<AddSingleQuestionConditionsProviderProps>,
) {
  const { children, chapterId, initialValues } = props;
  const [questionCount, setQuestionCount] = useState<QuestionCountType>({
    calculated: {},
    notCalculated: {},
  });

  const {
    structures,
    chapterData,
    levelItemsWithLevel,
    defaultLOS,
    checkExistOnStructures,
    isLoading,
  } = useGetSingleQuestionConditionsData({
    chapterId,
    questionTypeRow: initialValues?.data,
  });

  let transformQConfigTableData: AddSingleQuestionConditionsFormType | undefined;

  if (initialValues) {
    let transformToLocation;
    const contentType = initialValues.rows.find(
      (structure) => structure.parentId === initialValues.data.id,
    )?.category;
    if (contentType === 'TOPIC') {
      transformToLocation = initialValues?.rows.reduce(
        (acc, item) => {
          if (item.parentIds.includes(initialValues.data.id)) {
            const hasTopicInChildren = levelItemsWithLevel.some(
              (child) => child.level.category === QBankStructureLevelCategory.Topic,
            );
            switch (item.category) {
              case 'TOPIC':
                if (!hasTopicInChildren && chapterData?.refId) {
                  acc.topics[chapterData.refId] = true;
                } else {
                  acc.topics[item.refId] = true;
                }
                break;
              case 'SUB_TOPIC':
                acc.subtopics[item.refId] = true;
                break;
              case 'ALL_SUB_TOPIC': {
                const parentTopic = initialValues?.rows.find((row) => row.id === item.parentId);
                let allSubtopics;
                if (!hasTopicInChildren) {
                  allSubtopics = levelItemsWithLevel.filter(
                    (structure) => structure.parentLevelItemId === chapterData?.refId,
                  );
                } else {
                  allSubtopics = levelItemsWithLevel.filter(
                    (structure) => structure.parentLevelItemId === parentTopic?.refId,
                  );
                }
                if (allSubtopics) {
                  for (const subtopic of allSubtopics) {
                    acc.subtopics[subtopic.id] = true;
                  }
                }
                break;
              }
            }
          }
          return acc;
        },
        {
          topics: {} as Record<string, boolean>,
          subtopics: {} as Record<string, boolean>,
        },
      );
    }

    const transformToLOS = initialValues?.rows.find(
      (row) => row.category === 'LEARNING_OBJECTIVE_STATEMENT',
    );

    const scorePerQuestion =
      initialValues?.data.scorePerQuestion / initialValues?.data.totalQuestions;
    const location = transformToLocation ? { ...transformToLocation } : undefined;

    transformQConfigTableData = {
      contentType:
        contentType === QConfigStructureCategory.LearningObjectiveStatement ? 'LOS' : 'TOPIC',
      location: location ?? { topics: {}, subtopics: {} },
      los: {
        label: transformToLOS?.name ?? '',
        value: transformToLOS?.refId ?? '',
        inputValue: transformToLOS?.name,
      },
      questionType: {
        calculated: {
          easy: initialValues?.data.calculated.easy ?? null,
          medium: initialValues?.data.calculated?.medium ?? null,
          hard: initialValues?.data.calculated?.hard ?? null,
        },
        notCalculated: {
          easy: initialValues?.data.notCalculated?.easy ?? null,
          medium: initialValues?.data.notCalculated?.medium ?? null,
          hard: initialValues?.data.notCalculated?.hard ?? null,
        },
      },
      scorePerQuestion,
    };
  }

  const value: AddSingleQuestionConditionsContextType = useMemo(
    () => ({
      chapterId,
      chapterData,
      levelItemsWithLevel,
      checkExistOnStructures,
      questionCount: {
        get: questionCount,
        set: setQuestionCount,
      },
      defaultLOS: defaultLOS ?? '',
      structures,
    }),
    [
      chapterId,
      chapterData,
      levelItemsWithLevel,
      structures,
      checkExistOnStructures,
      questionCount,
      defaultLOS,
    ],
  );

  if (isLoading) {
    return (
      <Loading
        sx={{
          height: '100%',
          minHeight: '200px',
        }}
      />
    );
  }
  return (
    <AddSingleQuestionConditionsContext.Provider value={value}>
      <AddSingleQuestionConditionsFormProvider initialValues={transformQConfigTableData}>
        {children}
      </AddSingleQuestionConditionsFormProvider>
    </AddSingleQuestionConditionsContext.Provider>
  );
}
